import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  THARSTERN_BACK_END_CODE,
  type ItemBackEndMapping,
  type ItemMappings,
  type MappingBackEnd,
} from "@shared-core/data/fulfilment-route-types";
import {
  FIXED_ON_ITEM_LABEL,
  formatErroredLeafCount,
} from "@shared-core/domain/fulfilment-route-presentation";
import { Card, CardContent } from "../../primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { MappingLeafView } from "./MappingLeafView";
import { ArrowUpRight } from "lucide-react";

/**
 * The dictionary item page's Mappings tab: the item's mapping tree as its
 * code defines it (catalogue third-party fulfilment brief §8, journey D;
 * Ben, 24 Sep). "Ours" first, then a column per back-end — Tharstern, then
 * Solopress. The item row carries only each back-end's structure, with what
 * the back-end requires but our item does not vary on its own labelled
 * sub-row; then each field with its slot, then each option under its field
 * with its value, then any combinations a supplier maps together rather
 * than field by field. Values only: the one annotation is a red error mark
 * on a leaf that failed last night's run. Read-only — a mapping changes by PR.
 */
export function ItemMappingsPanel({
  dictionaryItemId,
}: {
  dictionaryItemId: string;
}) {
  const adapter = useDataAdapter();
  const [itemMappings, setItemMappings] = useState<ItemMappings | null>(null);
  const [isUnmapped, setIsUnmapped] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .getDictionaryItemMappings(dictionaryItemId)
      .then((res) => {
        if (res === null) setIsUnmapped(true);
        else setItemMappings(res);
      })
      .catch(() => setLoadError(true));
  }, [adapter, dictionaryItemId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadError) {
    return (
      <p className="text-sm text-destructive">
        Couldn&rsquo;t load these mappings.{" "}
        <button
          type="button"
          onClick={load}
          className="font-medium underline underline-offset-2"
        >
          Try again
        </button>
      </p>
    );
  }
  if (isUnmapped) {
    return (
      <p className="text-sm text-muted-foreground">
        No mappings are defined for this item yet. Mappings are defined in code;
        to add them, raise a PR.
      </p>
    );
  }
  if (!itemMappings) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  const { backEnds, item, fields, combinations, sourceUrl, itemFile } =
    itemMappings;
  const erroredCount = countErroredLeaves(itemMappings);
  // No Tharstern subtree means no Tharstern product type: a supplier-only
  // item (decision #71). Its Tharstern column holds nothing below the item
  // row's "Not made in-house".
  const isMadeInHouse = THARSTERN_BACK_END_CODE in item.byBackEnd;
  const isBlankColumn = (backEnd: MappingBackEnd) =>
    !isMadeInHouse && backEnd.code === THARSTERN_BACK_END_CODE;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm">
          Mappings are defined in code; to change one, raise a PR.{" "}
          {sourceUrl ? (
            <a
              href={sourceUrl}
              className="inline-flex items-center gap-1 font-medium underline underline-offset-2"
            >
              This item&rsquo;s code
              <ArrowUpRight className="size-3" />
            </a>
          ) : (
            <span className="font-mono text-xs text-muted-foreground">
              {itemFile}
            </span>
          )}
        </p>
        <p
          className={
            erroredCount > 0
              ? "text-sm font-medium text-destructive"
              : "text-sm text-muted-foreground"
          }
        >
          {formatErroredLeafCount(erroredCount)}
          {erroredCount > 0 &&
            " — marked in red below; that route is unavailable where it is used until a PR fixes it."}
        </p>
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[12rem]">Ours</TableHead>
                {backEnds.map((backEnd) => (
                  <TableHead key={backEnd.code} className="min-w-[14rem]">
                    {backEnd.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="align-top">
                <TableCell>
                  <p className="font-medium">
                    {itemMappings.dictionaryItemName}
                  </p>
                  <p className="text-xs text-muted-foreground">Item</p>
                </TableCell>
                {backEnds.map((backEnd) => (
                  <TableCell key={backEnd.code}>
                    <ItemLeaves
                      backEnd={backEnd}
                      row={item.byBackEnd[backEnd.code]}
                      part="structure"
                    />
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="align-top">
                <TableCell className="pl-8 text-xs text-muted-foreground">
                  {FIXED_ON_ITEM_LABEL}
                </TableCell>
                {backEnds.map((backEnd) => (
                  <TableCell key={backEnd.code}>
                    {!isBlankColumn(backEnd) && (
                      <ItemLeaves
                        backEnd={backEnd}
                        row={item.byBackEnd[backEnd.code]}
                        part="fixed"
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
              {fields.map((field) => (
                <FieldRows
                  key={field.label}
                  field={field}
                  backEnds={backEnds}
                  isBlankColumn={isBlankColumn}
                />
              ))}
              {combinations.length > 0 && (
                <CombinationRows
                  combinations={combinations}
                  backEnds={backEnds}
                />
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

/** Leaves that failed last night, across the whole tree and every back-end. */
function countErroredLeaves(mappings: ItemMappings): number {
  const itemErrors = Object.values(mappings.item.byBackEnd)
    .flatMap((row) => [...row.structure, ...row.fixed])
    .filter((l) => l.error).length;
  const fieldErrors = mappings.fields.flatMap((field) => [
    ...Object.values(field.byBackEnd).filter((s) => s.error),
    ...field.options.flatMap((o) =>
      Object.values(o.byBackEnd).filter((l) => l.error),
    ),
  ]).length;
  const combinationErrors = mappings.combinations
    .flatMap((c) => Object.values(c.byBackEnd))
    .filter((l) => l.error).length;
  return itemErrors + fieldErrors + combinationErrors;
}

/** One part of the item row on one back-end. With no subtree there, the
 *  structure cell says so plainly and the fixed cell stays empty. */
function ItemLeaves({
  backEnd,
  row,
  part,
}: {
  backEnd: MappingBackEnd;
  row: ItemBackEndMapping | undefined;
  part: "structure" | "fixed";
}) {
  if (!row) {
    return part === "structure" ? (
      <p className="text-sm text-muted-foreground">
        {backEnd.code === THARSTERN_BACK_END_CODE
          ? "Not made in-house"
          : `Not mapped on ${backEnd.label}`}
      </p>
    ) : (
      <MappingLeafView backEndLabel={backEnd.label} value={null} />
    );
  }
  const leaves = row[part];
  if (leaves.length === 0) {
    return <p className="text-sm text-muted-foreground">None</p>;
  }
  return (
    <div className="space-y-1">
      {leaves.map((l) => (
        <MappingLeafView
          key={l.attribute}
          backEndLabel={backEnd.label}
          attribute={l.attribute}
          value={l.value}
          error={l.error}
        />
      ))}
    </div>
  );
}

/** A field's own row (its slot per back-end), then a row per option. */
function FieldRows({
  field,
  backEnds,
  isBlankColumn,
}: {
  field: ItemMappings["fields"][number];
  backEnds: MappingBackEnd[];
  isBlankColumn: (backEnd: MappingBackEnd) => boolean;
}) {
  return (
    <>
      <TableRow className="align-top bg-muted/40">
        <TableCell>
          <p className="font-medium">{field.label}</p>
          <p className="text-xs text-muted-foreground">Field</p>
        </TableCell>
        {backEnds.map((backEnd) => {
          const slot = field.byBackEnd[backEnd.code];
          if (isBlankColumn(backEnd)) return <TableCell key={backEnd.code} />;
          return (
            <TableCell key={backEnd.code}>
              <MappingLeafView
                backEndLabel={backEnd.label}
                value={slot?.slot ?? null}
                error={slot?.error ?? null}
              />
            </TableCell>
          );
        })}
      </TableRow>
      {field.options.map((option) => (
        <TableRow key={option.label} className="align-top">
          <TableCell className="pl-8">{option.label}</TableCell>
          {backEnds.map((backEnd) => {
            const leaf = option.byBackEnd[backEnd.code];
            if (isBlankColumn(backEnd)) {
              return <TableCell key={backEnd.code} />;
            }
            return (
              <TableCell key={backEnd.code}>
                <MappingLeafView
                  backEndLabel={backEnd.label}
                  value={leaf?.value ?? null}
                  noEquivalentReason={leaf?.noEquivalentReason ?? null}
                  isOmitted={leaf?.isOmitted ?? false}
                  error={leaf?.error ?? null}
                />
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </>
  );
}

/** Options a supplier maps together (one of its values names two of our
 *  fields, e.g. a stand and its width). Each option's own row above still
 *  says what it maps to on its own. A back-end without the combination
 *  leaves its cell empty. */
function CombinationRows({
  combinations,
  backEnds,
}: {
  combinations: ItemMappings["combinations"];
  backEnds: MappingBackEnd[];
}) {
  return (
    <>
      <TableRow className="align-top bg-muted/40">
        <TableCell>
          <p className="font-medium">Combinations</p>
          <p className="text-xs text-muted-foreground">
            Options mapped together
          </p>
        </TableCell>
        {backEnds.map((backEnd) => (
          <TableCell key={backEnd.code} />
        ))}
      </TableRow>
      {combinations.map((combination) => {
        const label = combination.when
          .map((w) => `${w.fieldLabel}: ${w.optionLabel}`)
          .join(" + ");
        return (
          <TableRow key={label} className="align-top">
            <TableCell className="pl-8">{label}</TableCell>
            {backEnds.map((backEnd) => {
              const leaf = combination.byBackEnd[backEnd.code];
              return (
                <TableCell key={backEnd.code}>
                  {leaf && (
                    <MappingLeafView
                      backEndLabel={backEnd.label}
                      value={leaf.value}
                      noEquivalentReason={leaf.noEquivalentReason}
                      isOmitted={leaf.isOmitted}
                      error={leaf.error}
                    />
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </>
  );
}
